// Shared helper: compute prorated fixed-rate revenue for a period.
// sales_rate is treated as a MONTHLY amount; each day contributes
// (sales_rate * split_pct) / (days in that calendar month).
// When multiple fixed-rate rows overlap for the same (User, Project), the row
// with the latest date_from wins on each day, so a more recent contract
// supersedes an older one without double-counting the overlap.
//
// Returns:
//   - fixedRevenueByUserProject: { "user||project": revenue }
//   - fixedUserProjectKey: Set of "user||project||key" that have an overlapping
//     fixed rate in the period (used by callers to suppress hourly billing).

const MS_PER_DAY = 86400000;

// Number of days in the calendar month of the given UTC timestamp.
const daysInMonthUTC = (ms) => {
  const d = new Date(ms);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
};

const num = (x) => {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
};

const norm = (s) => String(s ?? "").trim();
const normKey = (s) => norm(s).toLowerCase();

const normProject = (s) =>
  norm(s)
    .toUpperCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Parse a YYYY-MM-DD (or similar) date string as a UTC midnight timestamp,
// avoiding local-timezone drift when comparing date-only values.
const parseDayUTC = (s) => {
  const [y, m, d] = String(s).split(/[-T/]/).map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
};

export function computeFixedRevenue(sellingRates, periodFrom, periodTo) {
  const fromMs = parseDayUTC(periodFrom);
  const toMs = parseDayUTC(periodTo);

  const ratesByUP = {};
  const fixedUserProjectKey = new Set();
  // Suppression must happen at the SAME granularity fixed revenue is added:
  // per (User, Project), NOT per (User, Project, Key). The per-key set broke
  // for hours imported from Chrona, whose export has no "Key" column (the task
  // code lives in "Issue"), so the hours' Key was "" and never matched the
  // rate's Key -> hourly billing leaked in on top of / instead of the fixed
  // rate. Keyed by (User, Project) it's robust to the Key column.
  const fixedUserProject = new Set();

  (sellingRates || []).forEach((r) => {
    if (!r.fixed) return;
    const rfMs = parseDayUTC(r.date_from);
    const rtMs = parseDayUTC(r.date_to);
    if (rfMs > toMs || rtMs < fromMs) return;

    const user = norm(r.User);
    const project = normProject(r.Project);
    if (!user || !project) return;

    fixedUserProjectKey.add(`${user}||${project}||${normKey(r.Key)}`);
    fixedUserProject.add(`${user}||${project}`);

    const up = `${user}||${project}`;
    (ratesByUP[up] = ratesByUP[up] || []).push({
      dateFromMs: rfMs,
      dateToMs: rtMs,
      monthlyRate: num(r.sales_rate),
      splitFraction: num(r.split_pct ?? 100) / 100,
      // Kept for human-readable overlap warnings below.
      rawFrom: r.date_from,
      rawTo: r.date_to,
    });
  });

  // ---- Defensive: detect overlapping fixed rows for the same (User, Project)
  // Two fixed rows whose date ranges intersect are almost always a data-entry
  // mistake (e.g. a split row left in by accident). The day loop below silently
  // keeps only the latest-date_from row per day, so an overlap can quietly
  // change revenue. Surface it loudly so it's caught before it skews a margin.
  const fixedOverlaps = [];
  Object.entries(ratesByUP).forEach(([up, rates]) => {
    for (let i = 0; i < rates.length; i++) {
      for (let j = i + 1; j < rates.length; j++) {
        const a = rates[i], b = rates[j];
        if (a.dateFromMs <= b.dateToMs && b.dateFromMs <= a.dateToMs) {
          const [user, project] = up.split("||");
          fixedOverlaps.push({
            user, project,
            a: { from: a.rawFrom, to: a.rawTo, monthly: a.monthlyRate, split: a.splitFraction * 100 },
            b: { from: b.rawFrom, to: b.rawTo, monthly: b.monthlyRate, split: b.splitFraction * 100 },
          });
          // eslint-disable-next-line no-console
          console.warn(
            `[Margix] Overlapping fixed rates for ${user} @ ${project}: ` +
            `${a.rawFrom}→${a.rawTo} (${a.monthlyRate}/mo, ${a.splitFraction * 100}%) overlaps ` +
            `${b.rawFrom}→${b.rawTo} (${b.monthlyRate}/mo, ${b.splitFraction * 100}%). ` +
            `Revenue uses the row with the latest start date per day — check the rates Excel.`
          );
        }
      }
    }
  });

  const fixedRevenueByUserProject = {};

  Object.entries(ratesByUP).forEach(([up, rates]) => {
    rates.sort((a, b) => b.dateFromMs - a.dateFromMs);

    let total = 0;
    for (let t = fromMs; t <= toMs; t += MS_PER_DAY) {
      const active = rates.find((r) => r.dateFromMs <= t && r.dateToMs >= t);
      if (active) {
        total += (active.monthlyRate * active.splitFraction) / daysInMonthUTC(t);
      }
    }
    fixedRevenueByUserProject[up] = total;
  });

  return { fixedRevenueByUserProject, fixedUserProjectKey, fixedUserProject, fixedOverlaps };
}
